import { useMemo } from 'react';
import { calcProPercentsBasedOnFilterAndDays, getPercentageByTotal, itemPercentage, projectionPerMonth } from '../utilis/common';
import { HOSPITAL_CARDS_TYPE } from '../types/hospital.type';
import { PAGE_TYPE } from '../types/pages.type';

/**
 * Custom hook for AI card data processing and percentage calculations
 * Provides card data with percentages, custom cards, and card metadata
 * IMPORTANT: This hook is page-aware and returns different card structures based on pageType
 */
export const useAICardDataContext = ({
  pageType, // CRITICAL: Determines which cards to include (HOSPITAL, COMMUNITY_TRANSFER, DECEASED, or ADMISSION)
  filterListData,
  filter,
  transferType,
  projectionDays,
  cardFilter,
  percentageBy,
  lockedTotalBy,
  specialComparisonSet,
  filterTotal,
  percentageAgainst,
  dbData,
  getPriorityNumber,
  dynamicCards,
  patientList,
  HOSPITAL_CUSTOM_CARD_DATA, // For Hospital page
  COMMUNITY_CUSTOM_CARD_DATA, // For Community Transfer page
  ADMISSION_CUSTOM_CARD_DATA // For Admission page
}) => {
  return useMemo(() => {
    // Helper function to calculate percentages like the cards do
    const calculateCardPercentages = (items, percentageBase, cardType, isFirstPriority = false) => {
      return items?.map(item => {
        // Determine if percentage should be scaled based on priority filters and transfer type
        const isComparingAgainstAvgCensus = filterListData?.isComparingAgainstAvgCensus;
        const isFirstItemInPriorityData = isFirstPriority;
        const shouldScale = isComparingAgainstAvgCensus || 
                           lockedTotalBy === "census" || 
                           (isFirstItemInPriorityData && (!transferType || transferType?.length === 0));
        
        // Use the actual card logic: item.total for value, item.percentage for pre-calculated percentage
        const actualValue = item.total || item.value || 0;
        
        // The backend already calculated the correct percentage based on totalForPercentage
        // We should use that pre-calculated percentage directly, not recalculate it
        const preCalculatedPercentage = item.percentage || 0;
        
        // Scale the percentage the same way cards do - using the pre-calculated percentage
        const scaledPercentage = calcProPercentsBasedOnFilterAndDays(
          preCalculatedPercentage,
          { ...filter, transferType },
          projectionDays,
          true,
          shouldScale
        );
        
        // Scale the value the same way cards do
        const scaledValue = calcProPercentsBasedOnFilterAndDays(actualValue, filter, projectionDays);
        
        return {
          ...item,
          actualValue, // CRITICAL: This is the EXACT count - AI should use this for "how many" questions
          calculatedPercentage: preCalculatedPercentage,
          scaledPercentage: `${scaledPercentage}%`,
          scaledValue, // WARNING: This may be scaled/rounded - do NOT use for exact counts
          projectedValue: projectionPerMonth(actualValue, filter, projectionDays),
          projectedPercentage: itemPercentage(projectionPerMonth(actualValue, filter, projectionDays), percentageBase, "number"),
          percentageContext: {
            base: percentageBase,
            shouldScale,
            cardType,
            isFirstPriority,
            transferType: transferType || 'all',
            usesPreCalculatedPercentage: !!item.percentage,
            backendCalculatedPercentage: preCalculatedPercentage
          }
        };
      }) || [];
    };

    // Get the correct percentage base (same logic as cards use)
    // This should match exactly what the cards use: specialComparisonSet ? filterTotal : getPercentageByTotal(dbData, pageType)
    // CRITICAL: Pass pageType to ensure correct baseline (bed capacity for Admission, census for Hospital, etc.)
    const currentPercentageBase = specialComparisonSet ? filterTotal : getPercentageByTotal(dbData, pageType);
    
    // Determine page type
    const isHospitalPage = pageType === PAGE_TYPE.HOSPITAL;
    const isCommunityTransferPage = pageType === PAGE_TYPE.COMMUNITY_TRANSFER;
    const isDeceasedPage = pageType === PAGE_TYPE.DECEASED;
    const isAdmissionPage = pageType === PAGE_TYPE.ADMISSION;
    
    const cardData = {
      // HOSPITAL PAGE ONLY: Hospitalizations data
      ...(isHospitalPage && {
        hospitalizations: {
        new: filterListData?.hospitalizations?.find(item => item.label === 'New Hospitalizations') || null,
        reHospitalizations: filterListData?.hospitalizations?.find(item => item.label === 'Re-Hospitalizations') || null,
        total: filterListData?.hospitalizations?.reduce((sum, item) => sum + (item.total || item.value || 0), 0) || 0,
        itemsWithPercentages: calculateCardPercentages(
          filterListData?.hospitalizations, 
          currentPercentageBase, 
          'hospitalizations',
          getPriorityNumber(HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS) === 1
        ),
        percentageMethod: percentageBy,
        percentageBase: currentPercentageBase,
        priorityInfo: {
          isFirstPriority: getPriorityNumber(HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS) === 1,
          priorityNumber: getPriorityNumber(HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS),
          hasActiveFilters: cardFilter?.hospitalizations?.length > 0
        },
        // CRITICAL: Chart data context for hospitalizations
        chartContext: {
          cardType: "hospitalizations",
          description: "Time-based analysis of new vs re-hospitalizations",
          availableChartViews: ["daily", "weekly", "7-day", "30-day", "monthly", "quarterly", "yearly"],
          chartDataInterpretation: {
            purpose: "Track hospitalization patterns over time to identify trends in new admissions vs readmissions",
            keyMetrics: ["Total hospitalizations per period", "New vs re-hospitalization ratio", "Percentage of total transfers"],
            trendAnalysis: "Look for patterns in readmission rates, seasonal variations, and overall hospitalization trends"
          },
          commonQuestions: [
            "What's the trend in hospitalizations over the last month?",
            "How many new hospitalizations were there last week?", 
            "What's the re-hospitalization rate trend?",
            "Show me daily hospitalization patterns",
            "Compare this month's hospitalizations to last month"
          ]
        }
      }
      }),
      
      // HOSPITAL PAGE ONLY: DC/ER data
      ...(isHospitalPage && {
        dcer: {
        dc: filterListData?.DCERData?.find(item => item.label === 'DC') || null,
        er: filterListData?.DCERData?.find(item => item.label === 'ER') || null,
        total: filterListData?.DCERData?.reduce((sum, item) => sum + (item.total || item.value || 0), 0) || 0,
        itemsWithPercentages: calculateCardPercentages(filterListData?.DCERData, currentPercentageBase),
        percentageMethod: percentageBy,
        percentageBase: currentPercentageBase,
        // IMPORTANT: Terminology definitions for AI
        terminology: {
          dc: {
            label: "DC",
            meaning: "Discharge - Patients who were ADMITTED to the hospital",
            description: "These are actual hospital discharges (wasAdmitted: true)",
            keyTerms: ["discharge", "discharges", "discharged", "admitted", "hospitalization"]
          },
          er: {
            label: "ER", 
            meaning: "Emergency Room - Patients who went to ER but were NOT admitted",
            description: "These are ER visits without hospital admission (wasAdmitted: false)",
            keyTerms: ["er", "emergency", "emergency room", "not admitted", "er visit"]
          }
        },
        // CRITICAL: Chart data context for DC/ER
        chartContext: {
          cardType: "dcer",
          description: "Time-based analysis of discharges (admitted patients) vs ER visits (non-admitted)",
          availableChartViews: ["daily", "weekly", "7-day", "30-day", "monthly", "quarterly", "yearly"],
          chartDataInterpretation: {
            purpose: "Track admission patterns and ER utilization over time",
            keyMetrics: ["DC count per period", "ER count per period", "DC to ER ratio", "Admission rate percentage"],
            trendAnalysis: "Monitor admission rates, ER utilization patterns, and capacity management effectiveness"
          },
          commonQuestions: [
            "What's the trend in discharges over the last month?",
            "How many ER visits were there last week?",
            "What's the DC to ER ratio trend?",
            "Show me daily discharge patterns",
            "Compare admission rates this month vs last month",
            "When do we see the most ER visits?"
          ]
        }
      }
      }),
      
      // SHARED: Insurance data (Hospital, Community Transfer, and Deceased pages)
      insurance: {
        items: filterListData?.insuranceData || [],
        total: filterListData?.insuranceData?.reduce((sum, item) => sum + (item.total || item.value || 0), 0) || 0,
        topInsurance: filterListData?.insuranceData?.slice().sort((a, b) => (b.total || 0) - (a.total || 0)).slice(0, 5) || [],
        itemsWithPercentages: calculateCardPercentages(filterListData?.insuranceData, currentPercentageBase),
        percentageMethod: percentageBy,
        percentageBase: currentPercentageBase
      },
      
      // SHARED: Returns data (Hospital and Community Transfer pages ONLY, NOT Deceased)
      ...((isHospitalPage || isCommunityTransferPage) && {
        returns: {
        returned: filterListData?.returnsData?.find(item => item.label === 'Returned') || null,
        didntReturn: filterListData?.returnsData?.find(item => item.label === "Didn't Return") || null,
        total: filterListData?.returnsData?.reduce((sum, item) => sum + (item.total || item.value || 0), 0) || 0,
        itemsWithPercentages: calculateCardPercentages(filterListData?.returnsData, percentageAgainst), // Returns use different base
        percentageMethod: percentageBy,
        percentageBase: percentageAgainst
      }
      }),
      
      // HOSPITAL, DECEASED, AND COMMUNITY TRANSFER PAGES: 90 Days Analysis data
      ...((isHospitalPage || isDeceasedPage || isCommunityTransferPage) && {
        ninetyDays: {
        items: filterListData?.ninetyDaysData || [],
        total: filterListData?.ninetyDaysData?.reduce((sum, item) => sum + (item.total || item.value || 0), 0) || 0,
        oneToSevenDays: filterListData?.ninetyDaysData?.find(item => item.label === '1-7 Day') || null,
        eightToFourteenDays: filterListData?.ninetyDaysData?.find(item => item.label === '8-14 Da') || null,
        fifteenToThirtyDays: filterListData?.ninetyDaysData?.find(item => item.label === '15-30 D') || null,
        thirtyOneToSixtyDays: filterListData?.ninetyDaysData?.find(item => item.label === '31-60 D') || null,
        sixtyOneToNinetyDays: filterListData?.ninetyDaysData?.find(item => item.label === '61-90 D') || null,
        itemsWithPercentages: calculateCardPercentages(filterListData?.ninetyDaysData, currentPercentageBase),
        percentageMethod: percentageBy,
        percentageBase: currentPercentageBase
      }
      }),
      
      // COMMUNITY TRANSFER PAGE ONLY: 60 Days Analysis data
      ...(isCommunityTransferPage && {
        sixtyDays: {
        items: filterListData?.sixtyDaysData || [],
        total: filterListData?.sixtyDaysData?.reduce((sum, item) => sum + (item.total || item.value || 0), 0) || 0,
        oneToSevenDays: filterListData?.sixtyDaysData?.find(item => item.label === '1-7 Day') || null,
        eightToFourteenDays: filterListData?.sixtyDaysData?.find(item => item.label === '8-14 Da') || null,
        fifteenToThirtyDays: filterListData?.sixtyDaysData?.find(item => item.label === '15-30 D') || null,
        thirtyOneToSixtyDays: filterListData?.sixtyDaysData?.find(item => item.label === '31-60 D') || null,
        itemsWithPercentages: calculateCardPercentages(filterListData?.sixtyDaysData, currentPercentageBase),
        percentageMethod: percentageBy,
        percentageBase: currentPercentageBase
      }
      }),
      
      // SHARED: Floors data (Hospital, Community Transfer, and Deceased pages)
      floors: {
        items: filterListData?.floorsData || [],
        total: filterListData?.floorsData?.reduce((sum, item) => sum + (item.total || item.value || 0), 0) || 0,
        topFloors: filterListData?.floorsData?.slice().sort((a, b) => (b.total || 0) - (a.total || 0)).slice(0, 5) || [],
        itemsWithPercentages: calculateCardPercentages(filterListData?.floorsData, currentPercentageBase),
        percentageMethod: percentageBy,
        percentageBase: currentPercentageBase
      },
      
      // SHARED: Doctors data (Hospital, Community Transfer, and Deceased pages)
      doctors: {
        items: filterListData?.doctorData || [],
        total: filterListData?.doctorData?.reduce((sum, item) => sum + (item.total || item.value || 0), 0) || 0,
        topDoctors: filterListData?.doctorData?.slice().sort((a, b) => (b.total || 0) - (a.total || 0)).slice(0, 5) || [],
        itemsWithPercentages: calculateCardPercentages(filterListData?.doctorData, currentPercentageBase),
        percentageMethod: percentageBy,
        percentageBase: currentPercentageBase
      },
      
      // SHARED: Days data (Hospital, Community Transfer, and Deceased pages)
      days: {
        items: filterListData?.daysData || [],
        total: filterListData?.daysData?.reduce((sum, item) => sum + (item.total || item.value || 0), 0) || 0,
        dayBreakdown: {
          sunday: filterListData?.daysData?.find(item => item.label === 'Sun') || null,
          monday: filterListData?.daysData?.find(item => item.label === 'Mon') || null,
          tuesday: filterListData?.daysData?.find(item => item.label === 'Tue') || null,
          wednesday: filterListData?.daysData?.find(item => item.label === 'Wed') || null,
          thursday: filterListData?.daysData?.find(item => item.label === 'Thu') || null,
          friday: filterListData?.daysData?.find(item => item.label === 'Fri') || null,
          saturday: filterListData?.daysData?.find(item => item.label === 'Sat') || null
        },
        itemsWithPercentages: calculateCardPercentages(filterListData?.daysData, currentPercentageBase),
        percentageMethod: percentageBy,
        percentageBase: currentPercentageBase
      },
      
      // HOSPITAL & ADMISSION PAGES: Diagnosis data
      ...((isHospitalPage || isAdmissionPage) && {
        diagnosis: {
        items: filterListData?.dxData || [],
        total: filterListData?.dxData?.reduce((sum, item) => sum + (item.total || item.value || 0), 0) || 0,
        topDiagnoses: filterListData?.dxData?.slice().sort((a, b) => (b.total || 0) - (a.total || 0)).slice(0, 5) || [],
        itemsWithPercentages: calculateCardPercentages(filterListData?.dxData, currentPercentageBase),
        percentageMethod: percentageBy,
        percentageBase: currentPercentageBase
      }
      }),
      
      // HOSPITAL PAGE ONLY: Shift data
      ...(isHospitalPage && {
        shifts: {
        items: filterListData?.shiftData || [],
        total: filterListData?.shiftData?.reduce((sum, item) => sum + (item.total || item.value || 0), 0) || 0,
        morning: filterListData?.shiftData?.find(item => item.label === 'Morning') || null,
        evening: filterListData?.shiftData?.find(item => item.label === 'Evening') || null,
        night: filterListData?.shiftData?.find(item => item.label === 'Night') || null,
        itemsWithPercentages: calculateCardPercentages(filterListData?.shiftData, currentPercentageBase),
        percentageMethod: percentageBy,
        percentageBase: currentPercentageBase,
        // CRITICAL: Chart data context for shifts
        chartContext: {
          cardType: "shifts",
          description: "Time-based analysis of transfers by shift (Morning/Evening/Night)",
          availableChartViews: ["daily", "weekly", "7-day", "30-day", "monthly", "quarterly", "yearly"],
          chartDataInterpretation: {
            purpose: "Analyze shift patterns and staffing effectiveness over time",
            keyMetrics: ["Transfers per shift per period", "Shift distribution percentages", "Peak shift identification"],
            trendAnalysis: "Monitor shift workload distribution, identify staffing needs, and track operational efficiency",
            expectedPatterns: {
              morning: "Should typically be highest (40-60%) - planned transfers and discharges",
              evening: "Moderate activity (25-40%) - continued planned transfers",
              night: "Lowest activity (5-20%) - primarily emergency transfers only"
            }
          },
          commonQuestions: [
            "What's the shift pattern trend over the last month?",
            "Which shift is busiest on weekdays vs weekends?",
            "How has night shift activity changed?",
            "Show me daily shift distribution patterns",
            "Compare shift workload this month vs last month",
            "When do we see unusual night shift spikes?"
          ]
        }
      }
      }),
      
      // HOSPITAL PAGE ONLY: Nurses data
      ...(isHospitalPage && {
        nurses: (() => {
        const nurseData = filterListData?.nurseData || [];
        return {
          items: nurseData,
          total: nurseData.reduce((sum, item) => sum + (item.total || item.value || 0), 0),
          topNurses: nurseData.slice().sort((a, b) => (b.total || 0) - (a.total || 0)).slice(0, 5),
          itemsWithPercentages: calculateCardPercentages(nurseData, currentPercentageBase),
          percentageMethod: percentageBy,
          percentageBase: currentPercentageBase
        };
      })()
      }),
      
      // HOSPITAL & ADMISSION PAGES: Hospitals data (where residents were admitted from)
      ...((isHospitalPage || isAdmissionPage) && {
        hospitals: {
        items: filterListData?.hospitalData || [],
        total: filterListData?.hospitalData?.reduce((sum, item) => sum + (item.total || item.value || 0), 0) || 0,
        topHospitals: filterListData?.hospitalData?.slice().sort((a, b) => (b.total || 0) - (a.total || 0)).slice(0, 5) || [],
        itemsWithPercentages: calculateCardPercentages(filterListData?.hospitalData, percentageAgainst), // Hospitals use different base
        percentageMethod: percentageBy,
        percentageBase: percentageAgainst
      }
      }),
      
      // COMMUNITY TRANSFER PAGE ONLY: Safe Discharge Assisted Living data
      ...(isCommunityTransferPage && {
        safeDischargeAssLiv: {
        items: filterListData?.safeDischargeAssLivData || [],
        total: filterListData?.safeDischargeAssLivData?.reduce((sum, item) => sum + (item.total || item.value || 0), 0) || 0,
        topLocations: filterListData?.safeDischargeAssLivData?.slice().sort((a, b) => (b.total || 0) - (a.total || 0)).slice(0, 5) || [],
        itemsWithPercentages: calculateCardPercentages(filterListData?.safeDischargeAssLivData, currentPercentageBase),
        percentageMethod: percentageBy,
        percentageBase: currentPercentageBase
      }
      }),
      
      // COMMUNITY TRANSFER PAGE ONLY: SNF Facility data
      ...(isCommunityTransferPage && {
        snfFacility: {
        items: filterListData?.snfFacilityData || [],
        total: filterListData?.snfFacilityData?.reduce((sum, item) => sum + (item.total || item.value || 0), 0) || 0,
        topFacilities: filterListData?.snfFacilityData?.slice().sort((a, b) => (b.total || 0) - (a.total || 0)).slice(0, 5) || [],
        itemsWithPercentages: calculateCardPercentages(filterListData?.snfFacilityData, currentPercentageBase),
        percentageMethod: percentageBy,
        percentageBase: currentPercentageBase
      }
      }),

      // ADMISSION, DECEASED, AND OVERALL PAGES: Average Length of Stay data
      ...((isAdmissionPage || isDeceasedPage) && (() => {
        // First check standard averageStayDaysData accessor
        let avgLosData = filterListData?.averageStayDaysData;
        let alosSource = 'averageStayDaysData';

        // If not found, check for custom tab ALOS accessor (e.g., deceased page)
        if (!avgLosData || avgLosData.length === 0) {
          // Check for ALOS custom tab accessor patterns
          const alosAccessors = ['averageLengthOfStayCustomtab1', 'averageLengthOfStayCustomtab', 'avgLosData'];
          for (const accessor of alosAccessors) {
            if (filterListData?.[accessor] && filterListData[accessor].length > 0) {
              avgLosData = filterListData[accessor];
              alosSource = accessor;
              break;
            }
          }

          // Also check patientList.customTabs if still not found
          if ((!avgLosData || avgLosData.length === 0) && patientList?.customTabs) {
            const alosTab = patientList.customTabs.find(tab => {
              const title = (tab.title || tab.label || '').toLowerCase();
              return title.includes('average') && title.includes('length') && title.includes('stay');
            });
            if (alosTab?.accessor && filterListData?.[alosTab.accessor]) {
              avgLosData = filterListData[alosTab.accessor];
              alosSource = alosTab.accessor;
            }
          }
        }

        if (!avgLosData || avgLosData.length === 0) return {};

        const avgItem = avgLosData[0] || {};
        return {
          avgLos: {
            items: avgLosData,
            total: avgItem.total || avgItem.value || 0,
            originalTotal: avgItem.originalTotal ?? avgItem.total ?? avgItem.value ?? 0,
            count: avgLosData.length,
            average: avgItem.total || avgItem.value || 0,
            unit: 'days',
            itemsWithPercentages: calculateCardPercentages(avgLosData, currentPercentageBase),
            percentageMethod: percentageBy,
            percentageBase: currentPercentageBase,
            source: alosSource
          }
        };
      })()),

      // CRITICAL: Dynamic Cards (Custom Question-Based Cards) - SHARED
      dynamicCards: (() => {
        const dynamicCardData = {};
        dynamicCards?.forEach(card => {
          const accessor = card.accessor;
          const cardItems = filterListData?.[accessor] || [];
          
          dynamicCardData[accessor] = {
            id: card._id,
            accessor: accessor,
            title: card.title || card.label || card.tableLabel,
            description: card.description,
            aiDescription: card.aiDescription, // CRITICAL: AI-generated description for better understanding
            type: 'dynamic',
            questionType: card.customQuestionInputType,
            items: cardItems,
            total: cardItems.reduce((sum, item) => sum + (item.total || item.value || 0), 0),
            itemsWithPercentages: calculateCardPercentages(
              cardItems, 
              currentPercentageBase,
              accessor,
              getPriorityNumber(accessor) === 1
            ),
            percentageMethod: percentageBy,
            percentageBase: currentPercentageBase,
            priorityInfo: {
              isFirstPriority: getPriorityNumber(accessor) === 1,
              priorityNumber: getPriorityNumber(accessor),
              hasActiveFilters: cardFilter?.[accessor]?.length > 0
            },
            // CRITICAL: Custom Card Processing Logic (from updateFilterListData)
            processingLogic: {
              // Priority filter processing (from hospital-common.js or community-common.js based on page)
              priorityFilterProcessing: {
                description: "When custom cards are in priority filters, they use filterCustomPatientData to filter patient data",
                isInPriorityFilter: cardFilter?.priorityData?.some(p => p.type === accessor),
                priorityFilterIndex: cardFilter?.priorityData?.findIndex(p => p.type === accessor) + 1 || null,
                affectsOtherCards: cardFilter?.priorityData?.some(p => p.type === accessor) && cardFilter?.priorityData?.[0]?.type === accessor
              },
              // Dynamic card processing (from hospital-common.js or community-common.js based on page)
              dynamicCardProcessing: {
                description: "Dynamic cards are processed using processDynamicCard function with specific question input types",
                questionInputType: card.customQuestionInputType,
                validationOptions: card.validationOptions,
                customQuestionOptions: card.customQuestionOptions,
                timeRangeType: card.timeRangeType,
                processingFunction: "processDynamicCard",
                usesFilteredData: true,
                totalType: "TOTAL_TYPE.FILTER"
              },
              // Filter state management
              filterStateManagement: {
                isInNewSavedFilters: false, // This would be determined by updateFilterListData logic
                hasActiveCardFilter: cardFilter?.[accessor]?.length > 0,
                cardFilterValues: cardFilter?.[accessor] || [],
                affectsPatientFilterData: true
              }
            },
            // Chart context for dynamic cards
            chartContext: {
              cardType: "dynamic",
              customCardType: accessor,
              description: `Time-based analysis of ${card.title || card.label}`,
              availableChartViews: ["daily", "weekly", "7-day", "30-day", "monthly", "quarterly", "yearly"]
            }
          };
        });
        return dynamicCardData;
      })(),
      
      // CRITICAL: Custom Tabs (Complex Filter-Based Cards) - SHARED
      customTabs: (() => {
        const customTabData = {};
        patientList?.customTabs?.forEach(tab => {
          const accessor = tab.accessor;
          const tabItems = filterListData?.[accessor] || [];
          
          customTabData[accessor] = {
            id: tab._id,
            accessor: accessor,
            title: tab.title || tab.label,
            description: tab.description,
            aiDescription: tab.aiDescription, // CRITICAL: AI-generated description for better understanding
            type: tab.type, // 'combineTab' or other custom tab types
            tabType: tab.type === 'combineTab' ? 'Combined Dashboard Tab' : 'Custom Dashboard Tab',
            filters: tab.filters || [],
            operations: tab.operations || [],
            page: tab.page,
            dashboardType: tab.dashboardType,
            items: tabItems,
            total: tabItems.reduce((sum, item) => sum + (item.total || item.value || 0), 0),
            itemsWithPercentages: calculateCardPercentages(
              tabItems,
              currentPercentageBase,
              accessor,
              getPriorityNumber(accessor) === 1
            ),
            percentageMethod: percentageBy,
            percentageBase: currentPercentageBase,
            priorityInfo: {
              isFirstPriority: getPriorityNumber(accessor) === 1,
              priorityNumber: getPriorityNumber(accessor),
              hasActiveFilters: cardFilter?.[accessor]?.length > 0
            },
            // CRITICAL: Custom Tab Processing Logic (from updateFilterListData)
            processingLogic: {
              // Priority filter processing (lines 196-206 in hospital-common.js)
              priorityFilterProcessing: {
                description: "When custom tabs are in priority filters, they use getCustomTabsCards to process data",
                isInPriorityFilter: cardFilter?.priorityData?.some(p => p.type === accessor),
                priorityFilterIndex: cardFilter?.priorityData?.findIndex(p => p.type === accessor) + 1 || null,
                affectsOtherCards: cardFilter?.priorityData?.some(p => p.type === accessor) && cardFilter?.priorityData?.[0]?.type === accessor,
                usesFilterCustomPatientData: true
              },
              // Custom tab processing (lines 456-466 in hospital-common.js or community-common.js)
              customTabProcessing: {
                description: "Custom tabs are processed using getCustomTabsCards function with complex filter logic",
                processingFunction: "getCustomTabsCards",
                usesFilteredData: true,
                totalType: "TOTAL_TYPE.FILTER",
                requiresAdditionalData: {
                  ninetyDaysData: true,
                  dynamicCards: true,
                  diffDashboardPatients: true,
                  customCombineTabData: true
                },
                isCheckNewFilters: true,
                pageType: pageType // Dynamic based on actual page type (HOSPITAL, COMMUNITY_TRANSFER, etc.)
              },
              // Filter state management
              filterStateManagement: {
                isInNewSavedFilters: false, // This would be determined by updateFilterListData logic
                hasActiveCardFilter: cardFilter?.[accessor]?.length > 0,
                cardFilterValues: cardFilter?.[accessor] || [],
                affectsPatientFilterData: true
              }
            },
            // CRITICAL: Combine tab logic explanation
            combineTabLogic: tab.type === 'combineTab' ? {
              description: "Combine tabs allow merging data from multiple dashboards with operations",
              filters: tab.filters?.map((filter, index) => ({
                dashboard: filter.dashboard,
                operation: tab.operations?.[index - 1] || (index === 0 ? 'none' : 'plus'),
                filterCount: filter.filters?.length || 0
              })),
              operations: tab.operations,
              operationExplanation: {
                plus: "Adds/unions patient data from different filters",
                minus: "Subtracts patient data (removes patients from first filter that match second filter)"
              },
              totalByPage: tabItems[0]?.totalByPage || {},
              calculation: "First filter establishes base, then operations are applied sequentially"
            } : null,
            // Chart context for custom tabs
            chartContext: {
              cardType: "customTab",
              customCardType: accessor,
              description: `Time-based analysis of ${tab.title || tab.label}`,
              availableChartViews: ["daily", "weekly", "7-day", "30-day", "monthly", "quarterly", "yearly"],
              isMultiDashboard: tab.type === 'combineTab' && tab.filters?.some(f => f.dashboard !== tab.page)
            }
          };
        });
        return customTabData;
      })()
    };

    return {
      cardData,
      currentPercentageBase,
      calculateCardPercentages
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageType,
    filterListData,
    filter,
    transferType,
    projectionDays,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst,
    dbData,
    getPriorityNumber,
    dynamicCards,
    patientList,
    HOSPITAL_CUSTOM_CARD_DATA,
    COMMUNITY_CUSTOM_CARD_DATA,
    ADMISSION_CUSTOM_CARD_DATA
  ]);
};

export default useAICardDataContext;
